import React, {useEffect, useState, useRef} from 'react';
import {
  AAMBeaconTableHeader,
  AAMBeaconTableRecord,
  AAMBeaconTable,
  AAMBeaconPagination
} from 'components/shared/Beacon';
import './BeaconTable.scss';
/**
 *  variant - how you want the beacon styling to be
 *  classtype - either dark or light depending on table design (light is set as default)
 *  id - for automated testing easier to get the table id
 *  rowsArray - set up as a 2D array so you can have multiple rows of # of records you need (i.e. 4 rows of 4 records or 10 rows of one record)
 *    * variant - how beacon sets style
 *    * record - string or html component displays
 *  headers - 1d array of table titles
 *    * id - for testing purposes
 *    * title - string or html component displays
 *    * variant - how beacon sets style
 *
 *    semi hard coded table that allows in header and rows Arrays to display custom elements
 */

const renderStickyRow = (rowsArray) => {
  return rowsArray.map((row, i) => (
      <td
        key={i}
        className={row.className || ''}
        colSpan={row.colSpan || 1}
      >
        <AAMBeaconTableRecord variant={row.variant}>
          {row.record}
        </AAMBeaconTableRecord>
      </td>
  ));
};

const renderRow = (rowsArray) => {
  return rowsArray.map((row, i) => (
    <td
      key={i}
      className={row.className || ''}
      colSpan={row.colSpan || 1}
    >
      <AAMBeaconTableRecord variant={row.variant}>
        {row.record}
      </AAMBeaconTableRecord>
    </td>
  ));
};

const LocalBeaconTable = ({
  variant = '',
  classType = 'beacon-on-light',
  rowsArray,
  headers,
  idName= "default",
  renderStickyRows = false,
  className = '',
  rowsPerPage = 100,
  defaultCurrentPage = 1,
  defaultStartPage = 0,
  defaultEndPage = rowsPerPage < rowsArray.length ? rowsPerPage : rowsArray.length,
  defaultCurrentPageRows = rowsArray.slice(defaultStartPage, defaultEndPage),
  type = '',

}) => {
  const totalRows = rowsArray.length;
  const [currentPageRows, setCurrentPageRows] = useState(defaultCurrentPageRows);
  const [currentPage, setCurrentPage] = useState(defaultCurrentPage);
  const tablePagination = useRef(false);

  useEffect(() => {
    const start = (currentPage - 1) * rowsPerPage;
    const end = currentPage * rowsPerPage < totalRows ? currentPage * rowsPerPage : totalRows;
    setCurrentPageRows(rowsArray.slice(start, end));
  }, [rowsArray]);

  useEffect(() => {
    if (defaultCurrentPage !== 1) {
      setCurrentPage(defaultCurrentPage);
    }
  }, [defaultCurrentPage]);

  useEffect(() => {
    if (tablePagination.current) {
      tablePagination.current.addEventListener('beacon-event-change', (event) => {
        setCurrentPage(event.detail.page);
        const start = (event.detail.page - 1) * rowsPerPage;
        const end = event.detail.page * rowsPerPage < totalRows ? event.detail.page * rowsPerPage : totalRows;
        setCurrentPageRows(rowsArray.slice(start, end));
      });
    }

    return () => {
      if (tablePagination.current) {
        tablePagination.current.removeEventListener('beacon-event-input', (event) => {
          const start = event.detail.page * rowsPerPage;
          const end = (event.detail.page + 1) * rowsPerPage < totalRows ? (event.detail.page + 1) * rowsPerPage : totalRows;
          setCurrentPageRows(rowsArray.slice(start, end));
        });
      }
    };
  }, [tablePagination.current]);

  const rowsArrayIsObj = rowsArray.every(item => !Array.isArray(item));

  return (
    <div>
      <div className={"cost-basis-table-overflow"}>
        <AAMBeaconTable variant={variant} class={classType} id={idName}>
        <table>
          <thead>
            <tr headers={'header-rows'}>
              {headers.map((header, i) => (
                <th id={header.id} scope={'col'} key={i} className={header.className}>
                  <AAMBeaconTableHeader
                    variant={header.variant}
                    sortable={header?.sortable}>
                    {header.title}
                  </AAMBeaconTableHeader>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentPageRows.map((row, i) => (
              <React.Fragment key={i}>
                <tr
                  headers={`body-rows-${i}`}
                  key={i}
                  className={rowsArrayIsObj ? row.className : className}
                  id={row.agreementID ? `${row.agreementID}-${type}` : null}
                >
                  {renderStickyRows
                      ? renderStickyRow(rowsArrayIsObj ? row.rowArray : row)
                      : renderRow(rowsArrayIsObj ? row.rowArray : row)}
                </tr>
                {
                  /*
                    TODO: replace with beacon stub functionality, currently is used to
                    optionally display child rows that do not count towards pagination
                  */
                  row.childRows && row.childRows.map((child, j) => (
                    <>
                    <tr
                      headers={`body-rows-${i}`}
                      key={j}
                      className={`${rowsArrayIsObj ? row.className : className} ${j !== row.childRows.length - 1 ? 'remove-border-block-end' : ''}`}
                    >
                      {renderStickyRows
                        ? renderStickyRow(rowsArrayIsObj ? child.rowArray : child)
                        : renderRow(rowsArrayIsObj ? child.rowArray : child)
                      }
                    </tr>
                      {
                        child.childRows && child.childRows.map((child2, j) => (
                          <tr
                            headers={`body-rows-${i}`}
                            key={j}
                            className={`${rowsArrayIsObj ? row.className : className} remove-border-block-end`}
                          >
                            {renderStickyRows
                              ? renderStickyRow(rowsArrayIsObj ? child2.rowArray : child2)
                              : renderRow(rowsArrayIsObj ? child2.rowArray : child2)
                            }
                          </tr>
                        ))
                      }
                      </>
                  ))
                }
                {
                  row.accordion && row.accordion.open && (
                    <tr>
                      <td colSpan={8}>
                        {row.accordion.component}
                      </td>
                    </tr>
                  )
                }
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </AAMBeaconTable>
      </div>
        { Math.ceil(totalRows / rowsPerPage) > 1 &&
          <div className="table-pagination-container">
            <AAMBeaconPagination
              ref={tablePagination}
              id={`default-pagination-${idName}`}
              pages={Math.ceil(totalRows / rowsPerPage)}
              current-page={currentPage}
              variant="input"
            ></AAMBeaconPagination>
          </div>
        }
    </div>
  );
};

export default LocalBeaconTable;
